import { useEffect, useRef, useState } from 'react';
import './HeroSection.css';

/* ── CHANNEL SLIDES ─────────────────────────────────────
   The hero crossfades between these. Each slide swaps the
   headline, typed words, subtext, ticks, mockup and stats. */
const SLIDES = [
  {
    id: 'whatsapp',
    prefix: 'Turn WhatsApp Into',
    words: ['Your Best Sales Rep', 'A 24/7 Support Bot', 'A Lead Machine', 'Your Growth Engine'],
    sub: 'Zepliq connects AI to your WhatsApp, so every customer gets an instant, intelligent reply, day or night. From lead capture to order confirmation, it handles the entire conversation.',
    ticks: [
      'No WhatsApp Business API required, scan a QR code',
      'AI trained on your products, FAQs and policies',
      'Shopify & WooCommerce order confirmation built-in',
      'Lead pipeline, broadcasts & human takeover included',
    ],
    mockup: 'whatsapp',
    floats: [
      { icon: '📈', num: '10x',  label: 'More Leads' },
      { icon: '⚡', num: '<1s',  label: 'Response Time' },
      { icon: '🤖', num: '24/7', label: 'AI Support' },
    ],
  },
  {
    id: 'email',
    prefix: 'Turn Email Into',
    words: ['Your Inbox Autopilot', 'A 24/7 Reply Engine', 'Zero Unread Threads', 'Your Fastest Rep'],
    sub: 'Zepliq connects AI to your Gmail, reading every incoming email and drafting an on-brand reply in seconds. You approve it, or let it send, so your inbox never sleeps.',
    ticks: [
      'Connects securely to Gmail with Google OAuth',
      'AI drafts context-aware replies for your approval',
      'Auto-organises and prioritises your inbox',
      'Send on approval, or fully hands-free replies',
    ],
    mockup: 'email',
    floats: [
      { icon: '✉️', num: '90%',  label: 'Faster Replies' },
      { icon: '⚡', num: '<1s',  label: 'Draft Ready' },
      { icon: '📥', num: '0',    label: 'Unread Left' },
    ],
  },
];

const SLIDE_MS = 6500; // time each channel slide stays before advancing

export default function HeroSection({ onWatchDemo }) {
  const [slideIndex, setSlideIndex] = useState(0);
  const [wordIndex, setWordIndex]   = useState(0);
  const [displayed, setDisplayed]   = useState('');
  const [deleting, setDeleting]     = useState(false);
  const [fading, setFading]         = useState(false);
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  const slide = SLIDES[slideIndex];

  /* ── SLIDE CROSSFADE / AUTO-ADVANCE ──────────────────── */
  const changeSlide = (next) => {
    if (next === slideIndex) return;
    setFading(true);
    setTimeout(() => {
      setSlideIndex(next);
      setDisplayed('');
      setDeleting(false);
      setWordIndex(0);
      setFading(false);
    }, 350);
  };

  useEffect(() => {
    const id = setTimeout(() => changeSlide((slideIndex + 1) % SLIDES.length), SLIDE_MS);
    return () => clearTimeout(id);
    // changeSlide is intentionally excluded — it changes every render and would reset the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideIndex]);

  /* ── TYPEWRITER ─────────────────────────────────────── */
  useEffect(() => {
    const words = slide.words;
    const word  = words[wordIndex];
    let timeout;

    if (!deleting && displayed.length < word.length) {
      timeout = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), 80);
    } else if (!deleting && displayed.length === word.length) {
      timeout = setTimeout(() => setDeleting(true), 1600);
    } else if (deleting && displayed.length > 0) {
      timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 45);
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setWordIndex(i => (i + 1) % words.length);
    }
    return () => clearTimeout(timeout);
  }, [displayed, deleting, wordIndex, slide.words]);

  /* ── PARTICLE CANVAS ─────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, particles = [];
    const PARTICLE_COUNT = 55;

    const resize = () => {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x  = Math.random() * W;
        this.y  = Math.random() * H;
        this.r  = Math.random() * 1.8 + 0.4;
        this.dx = (Math.random() - 0.5) * 0.25;
        this.dy = (Math.random() - 0.5) * 0.25;
        this.o  = Math.random() * 0.5 + 0.15;
        this.hue = Math.random() > 0.6 ? 245 : 270; // indigo or violet
      }
      update() {
        this.x += this.dx; this.y += this.dy;
        if (this.x < 0 || this.x > W || this.y < 0 || this.y > H) this.reset();
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, 80%, 55%, ${this.o})`;
        ctx.fill();
      }
    }

    for (let i = 0; i < PARTICLE_COUNT; i++) particles.push(new Particle());

    const drawLines = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(99,102,241,${0.06 * (1 - d / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => { p.update(); p.draw(); });
      drawLines();
      animRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  const fadeClass = fading ? ' is-fading' : '';

  return (
    <section className="hero" aria-label="Hero">
      {/* Particle canvas */}
      <canvas ref={canvasRef} className="hero__canvas" aria-hidden="true" />

      {/* Ambient blobs */}
      <div className="hero__blob hero__blob--green" aria-hidden="true" />
      <div className="hero__blob hero__blob--blue"  aria-hidden="true" />

      <div className="container hero__inner">
        {/* Left: copy */}
        <div className="hero__copy">

          <div className="hero__badge-row">
            <span className="badge badge-brand">
              <span className="hero__pulse" aria-hidden="true" />
              Trusted by 5,000+ businesses worldwide
            </span>
          </div>

          <div className={`hero__slide${fadeClass}`}>
            <h1 className="hero__headline">
              {slide.prefix}<br />
              <span className="gradient-text">{displayed}<span className="hero__cursor" aria-hidden="true">|</span></span>
            </h1>

            <p className="hero__sub text-xl">{slide.sub}</p>

            {/* Feature ticks */}
            <ul className="hero__ticks">
              {slide.ticks.map(t => (
                <li key={t} className="hero__tick">
                  <span className="hero__tick-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="10" fill="rgba(99,102,241,0.15)" />
                      <path d="M6 10l3 3 5-5" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* CTAs */}
          <div className="hero__ctas">
            <a href="https://dashboard.zepliq.com" className="btn btn-primary btn-lg">
  Start Free
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
</a>

            <button onClick={onWatchDemo} className="btn btn-secondary btn-lg">
              <span className="hero__play" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </span>
              See How It Works
            </button>
          </div>

          {/* Channel slide indicators */}
          <div className="hero__dots" role="tablist" aria-label="Channels">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={i === slideIndex}
                aria-label={s.id === 'whatsapp' ? 'WhatsApp automation' : 'Email automation'}
                className={`hero__dot${i === slideIndex ? ' is-active' : ''}`}
                onClick={() => changeSlide(i)}
              >
                <span className="hero__dot-label">{s.id === 'whatsapp' ? 'WhatsApp' : 'Email'}</span>
              </button>
            ))}
          </div>

        </div>

        {/* Right: mockup */}
        <div className="hero__visual" aria-hidden="true">
          <div className={`hero__phone-wrap hero__slide${fadeClass}`}>
            <div className="hero__phone">
              <div className="hero__phone-notch" />
              <div className="hero__phone-screen">
                {slide.mockup === 'whatsapp' ? (
                  <>
                    {/* WhatsApp chat mockup */}
                    <div className="hero__chat-header">
                      <div className="hero__chat-avatar">R</div>
                      <div>
                        <div className="hero__chat-name">Zepliq Bot</div>
                        <div className="hero__chat-status">● Online · 24/7</div>
                      </div>
                    </div>
                    <div className="hero__chat-body">
                      <div className="hero__msg hero__msg--bot">
                        👋 Hi! Welcome to our store. How can I help you today?
                      </div>
                      <div className="hero__msg hero__msg--user">
                        I want to place an order 🛒
                      </div>
                      <div className="hero__msg hero__msg--bot">
                        Great! Browse our catalogue below 👇
                        <div className="hero__msg-buttons">
                          <span>📦 Products</span>
                          <span>🏷️ Offers</span>
                        </div>
                      </div>
                      <div className="hero__msg hero__msg--typing">
                        <span /><span /><span />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Email (Gmail) mockup */}
                    <div className="hero__chat-header">
                      <div className="hero__chat-avatar hero__mail-logo">✦</div>
                      <div>
                        <div className="hero__chat-name">Email Assistant</div>
                        <div className="hero__chat-status">● Connected to Gmail</div>
                      </div>
                    </div>
                    <div className="hero__mail-list">
                      <div className="hero__mail-row hero__mail-row--unread">
                        <span className="hero__mail-dot" />
                        <div className="hero__mail-meta">
                          <div className="hero__mail-from">Sarah · New customer</div>
                          <div className="hero__mail-subj">Same-day delivery available?</div>
                        </div>
                        <div className="hero__mail-time">2m</div>
                      </div>
                      <div className="hero__mail-row">
                        <span className="hero__mail-dot hero__mail-dot--muted" />
                        <div className="hero__mail-meta">
                          <div className="hero__mail-from">James · Order #1042</div>
                          <div className="hero__mail-subj">Can I change my shipping address?</div>
                        </div>
                        <div className="hero__mail-time">9m</div>
                      </div>
                    </div>
                    <div className="hero__mail-draft">
                      <div className="hero__mail-badge">✨ AI draft reply</div>
                      <div className="hero__mail-draft-body">
                        Hi Sarah! Yes — order before 3&nbsp;pm and we’ll ship it for same-day delivery. Want me to send the checkout link?
                      </div>
                      <div className="hero__mail-draft-actions">
                        <span className="hero__mail-btn hero__mail-btn--primary">Approve &amp; send</span>
                        <span className="hero__mail-btn">Edit</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Floating stat cards */}
            {slide.floats.map((f, i) => (
              <div key={f.label} className={`hero__float-card hero__float-card--${i + 1}`}>
                <div className="hero__float-icon">{f.icon}</div>
                <div>
                  <div className="hero__float-num">{f.num}</div>
                  <div className="hero__float-label">{f.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="hero__scroll" aria-hidden="true">
        <span className="hero__scroll-line" />
      </div>
    </section>
  );
}
